import React, { useEffect, useMemo, useState } from "react";
import { GeoJsonLayer } from "@deck.gl/layers";
import { addLayer } from "../../../../utils/addingLayers/AddVoltagesLayers";

const norm = (value) =>
    value === null || value === undefined
        ? ""
        : String(value).trim().toLowerCase();

const getFeatures = (layer) => {
    const data = layer?.props?.data;

    if (data?.features && Array.isArray(data.features)) return data.features;
    if (Array.isArray(data)) return data;

    return [];
};

const findLayer = (layers, aliases) =>
    layers.find((layer) => {
        const id = String(layer?.id || "").toLowerCase();
        return aliases.some((alias) => id.includes(alias));
    });

const firstValue = (props, keys) => {
    for (const key of keys) {
        const value = props?.[key];
        if (value !== null && value !== undefined && value !== "") return value;
    }
    return null;
};

const safeId = (value) =>
    String(value || "")
        .replace(/\s+/g, "_")
        .replace(/[^\w-]/g, "");

const HierarchyDisplay = ({
    isBrojilaActive,
    wfsLayers,
    setWfsLayers,
    setHoverInfo,
    handleClickInfo,
    setWmsLayers,
    viewState,
    setViewState,
    setOverlay,
    id_terenske_jedinice,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [expandedVods, setExpandedVods] = useState([]);
    const [expandedTrafos, setExpandedTrafos] = useState([]);
    const [selectedMeters, setSelectedMeters] = useState([]);

    const mjernaLayer = useMemo(
        () => findLayer(wfsLayers, ["mjerna_mjesta"]),
        [wfsLayers]
    );

    const trafoLayer = useMemo(
        () => findLayer(wfsLayers, ["trafo_stanice"]),
        [wfsLayers]
    );

    const vodLayer = useMemo(
        () => findLayer(wfsLayers, ["vod", "dionica"]),
        [wfsLayers]
    );

    useEffect(() => {
        const loadRequiredLayers = async () => {
            if (!isBrojilaActive) return;

            const missingLayers = [];

            if (!trafoLayer) missingLayers.push("trafo_stanice");
            if (!vodLayer) missingLayers.push("vod");

            if (!missingLayers.length) return;

            setIsLoading(true);

            try {
                const responses = await Promise.all(
                    missingLayers.map((layerName) =>
                        addLayer(
                            layerName,
                            setHoverInfo,
                            handleClickInfo,
                            null,
                            setWmsLayers,
                            null,
                            viewState,
                            setViewState,
                            false,
                            setOverlay,
                            id_terenske_jedinice,
                            wfsLayers
                        )
                    )
                );

                const newLayers = responses.flat().filter(Boolean);

                if (newLayers.length) {
                    setWfsLayers((prev) => {
                        const ids = new Set(prev.map((layer) => layer.id));
                        return [
                            ...prev,
                            ...newLayers.filter((layer) => !ids.has(layer.id)),
                        ];
                    });
                }
            } catch (error) {
                console.error("Greška pri učitavanju hijerarhije:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadRequiredLayers();
    }, [isBrojilaActive, trafoLayer, vodLayer]);

    const hierarchyData = useMemo(() => {
        const mjernaMjesta = getFeatures(mjernaLayer);
        const trafostanice = getFeatures(trafoLayer);
        const vodovi = getFeatures(vodLayer);

        const metersByTrafo = new Map();
        const meterFeatureByLabel = new Map();

        for (const mm of mjernaMjesta) {
            const p = mm.properties || {};

            const trafoKey = norm(
                firstValue(p, [
                    "trafostanica",
                    "naziv_trafo_stanice",
                    "naziv_ts",
                    "id_trafo_stanice",
                    "id_ts",
                    "ts",
                ])
            );

            if (!trafoKey) continue;

            const meterLabel =
                firstValue(p, [
                    "id_mm",
                    "id_mjernog_mjesta",
                    "broj_brojila",
                    "serijski_broj",
                    "naziv",
                    "id",
                ]) || "Nepoznato brojilo";

            if (!metersByTrafo.has(trafoKey)) {
                metersByTrafo.set(trafoKey, []);
            }

            metersByTrafo.get(trafoKey).push(meterLabel);
            meterFeatureByLabel.set(String(meterLabel), mm);
        }

        const trafosByVod = new Map();
        const trafoFeatureByLabel = new Map();

        for (const trafo of trafostanice) {
            const p = trafo.properties || {};

            const vodKey = norm(
                firstValue(p, [
                    "pripadnost_dv",
                    "pripadnost_vodu",
                    "vod",
                    "naziv_voda",
                    "id_vod",
                    "dalekovod",
                ])
            );

            if (!vodKey) continue;

            const trafoLabel =
                firstValue(p, [
                    "naziv",
                    "naziv_trafo_stanice",
                    "naziv_ts",
                    "trafostanica",
                    "id_trafo_stanice",
                    "id",
                ]) || "Nepoznata trafostanica";

            const trafoKey = norm(trafoLabel);

            if (!trafosByVod.has(vodKey)) {
                trafosByVod.set(vodKey, []);
            }

            trafosByVod.get(vodKey).push({
                label: trafoLabel,
                key: trafoKey,
                meterCount: metersByTrafo.get(trafoKey)?.length || 0,
            });

            trafoFeatureByLabel.set(String(trafoLabel), trafo);
        }

        const vodFeatureByLabel = new Map();

        const vodList = vodovi
            .map((vod) => {
                const p = vod.properties || {};

                const label =
                    firstValue(p, [
                        "naziv_voda",
                        "naziv",
                        "ime",
                        "name",
                        "id_vod",
                        "id",
                    ]) || "Nepoznat vod";

                const key = norm(label);

                vodFeatureByLabel.set(String(label), vod);

                return {
                    label,
                    key,
                    trafoCount: trafosByVod.get(key)?.length || 0,
                };
            })
            .filter((vod) => vod.trafoCount > 0)
            .sort((a, b) => String(a.label).localeCompare(String(b.label), "sr"));

        for (const [, trafos] of trafosByVod) {
            trafos.sort((a, b) =>
                String(a.label).localeCompare(String(b.label), "sr")
            );
        }

        return {
            vodList,
            trafosByVod,
            metersByTrafo,
            vodFeatureByLabel,
            trafoFeatureByLabel,
            meterFeatureByLabel,
            counts: {
                brojila: mjernaMjesta.length,
                trafostanice: trafostanice.length,
                vodovi: vodovi.length,
            },
        };
    }, [mjernaLayer, trafoLayer, vodLayer]);

    const toggleHighlightLayer = (id, feature, type) => {
        if (!feature) return;

        const exists = wfsLayers.some((layer) => layer.id === id);

        if (exists) {
            setWfsLayers((prev) => prev.filter((layer) => layer.id !== id));
            return;
        }

        let color = [0, 120, 255, 255];

        if (type === "vod") color = [255, 255, 0, 230];
        if (type === "trafo") color = [255, 0, 0, 255];
        if (type === "brojilo") color = [0, 120, 255, 255];

        const layer = new GeoJsonLayer({
            id,
            data: {
                type: "FeatureCollection",
                features: [feature],
            },
            getLineColor: color,
            getFillColor: color,
            getPointRadius: 7,
            getLineWidth: 4,
            pointRadiusMinPixels: 5,
            lineWidthMinPixels: 3,
            lineWidthUnits: "pixels",
            radiusUnits: "pixels",
            pickable: true,
        });

        setWfsLayers((prev) => [...prev, layer]);
    };

    const toggleVod = (vod) => {
        toggleHighlightLayer(
            `hierarchy-vod-${safeId(vod.label)}`,
            hierarchyData.vodFeatureByLabel.get(String(vod.label)),
            "vod"
        );

        setExpandedVods((prev) =>
            prev.includes(vod.key)
                ? prev.filter((item) => item !== vod.key)
                : [...prev, vod.key]
        );
    };

    const toggleTrafo = (trafo) => {
        toggleHighlightLayer(
            `hierarchy-trafo-${safeId(trafo.label)}`,
            hierarchyData.trafoFeatureByLabel.get(String(trafo.label)),
            "trafo"
        );

        setExpandedTrafos((prev) =>
            prev.includes(trafo.key)
                ? prev.filter((item) => item !== trafo.key)
                : [...prev, trafo.key]
        );
    };

    const toggleMeter = (meter) => {
        toggleHighlightLayer(
            `hierarchy-meter-${safeId(meter)}`,
            hierarchyData.meterFeatureByLabel.get(String(meter)),
            "brojilo"
        );

        setSelectedMeters((prev) =>
            prev.includes(meter)
                ? prev.filter((item) => item !== meter)
                : [...prev, meter]
        );
    };

    return (
        <div>

            {isLoading && (
                <>
                    <div className="loading-spinner"></div>
                    <div style={{ color: "#ccc", fontSize: 13, marginTop: 8 }}>
                        Učitavam potrebne slojeve...
                    </div>
                </>
            )}

            {!isLoading && !isBrojilaActive && (
                <div className="hierarchy-empty">
                    Prvo uključi brojila.
                </div>
            )}

            {!isLoading && isBrojilaActive && (!trafoLayer || !vodLayer) && (
                <div className="hierarchy-empty">
                    Čekam učitavanje vodova i trafostanica...
                </div>
            )}

            {!isLoading && isBrojilaActive && trafoLayer && vodLayer && (
                <>
                    <div className="hierarchy-summary">
                        Vodovi: {hierarchyData.vodList.length} | Trafostanice:{" "}
                        {hierarchyData.counts.trafostanice} | Brojila:{" "}
                        {hierarchyData.counts.brojila}
                    </div>

                    {!hierarchyData.vodList.length ? (
                        <div className="hierarchy-empty">
                            Nema povezane hijerarhije. Provjeri atribute:
                            <br />
                            vod.id_vod / vod.naziv_voda
                            <br />
                            trafostanica.id_vod / trafostanica.pripadnost_dv
                            <br />
                            brojilo.trafostanica / brojilo.id_trafo_stanice
                        </div>
                    ) : (
                        <ul className="transformerAreaList">
                            {hierarchyData.vodList.map((vod) => {
                                const vodExpanded = expandedVods.includes(vod.key);
                                const trafos =
                                    hierarchyData.trafosByVod.get(vod.key) || [];

                                return (
                                    <li key={vod.key}>
                                        <span
                                            onClick={() => toggleVod(vod)}
                                            style={{
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                color: vodExpanded
                                                    ? "#ffffff"
                                                    : "#9e9d9d",
                                            }}
                                        >
                                            {vodExpanded ? "▼" : "▶"} {vod.label} (
                                            {vod.trafoCount})
                                        </span>

                                        {vodExpanded && (
                                            <ul className="sub-options">
                                                {trafos.map((trafo) => {
                                                    const trafoExpanded =
                                                        expandedTrafos.includes(
                                                            trafo.key
                                                        );

                                                    const meters =
                                                        hierarchyData.metersByTrafo.get(
                                                            trafo.key
                                                        ) || [];

                                                    return (
                                                        <li key={trafo.key}>
                                                            <span
                                                                onClick={() =>
                                                                    toggleTrafo(trafo)
                                                                }
                                                                style={{
                                                                    cursor: "pointer",
                                                                    fontWeight: "bold",
                                                                    color: trafoExpanded
                                                                        ? "#ffffff"
                                                                        : "#9e9d9d",
                                                                }}
                                                            >
                                                                {trafoExpanded
                                                                    ? "▼"
                                                                    : "▶"}{" "}
                                                                {trafo.label} (
                                                                {trafo.meterCount})
                                                            </span>

                                                            {trafoExpanded && (
                                                                <ul className="sub-options">
                                                                    {meters
                                                                        .slice(0, 200)
                                                                        .map((meter) => (
                                                                            <li
                                                                                key={String(
                                                                                    meter
                                                                                )}
                                                                            >
                                                                                <span
                                                                                    onClick={() =>
                                                                                        toggleMeter(
                                                                                            meter
                                                                                        )
                                                                                    }
                                                                                    style={{
                                                                                        cursor: "pointer",
                                                                                        color: selectedMeters.includes(
                                                                                            meter
                                                                                        )
                                                                                            ? "#ffffff"
                                                                                            : "#9e9d9d",
                                                                                    }}
                                                                                >
                                                                                    {String(
                                                                                        meter
                                                                                    )}
                                                                                </span>
                                                                            </li>
                                                                        ))}

                                                                    {meters.length >
                                                                        200 && (
                                                                            <li
                                                                                style={{
                                                                                    color: "#aaa",
                                                                                    fontSize: 12,
                                                                                    marginTop: 4,
                                                                                }}
                                                                            >
                                                                                Prikazano
                                                                                prvih 200 od{" "}
                                                                                {
                                                                                    meters.length
                                                                                }{" "}
                                                                                brojila.
                                                                            </li>
                                                                        )}

                                                                    {!meters.length && (
                                                                        <li
                                                                            style={{
                                                                                color: "#aaa",
                                                                                fontSize: 12,
                                                                            }}
                                                                        >
                                                                            Nema
                                                                            povezanih
                                                                            brojila.
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
};

export default HierarchyDisplay;